import { useState, useEffect } from 'react'

/**
 * Breakpoint definitions for consistent responsive design
 */
export const BREAKPOINTS = {
  /** Extra small phones */
  xs: 480,
  /** Small tablets / large phones */
  sm: 640,
  /** Tablets */
  md: 768,
  /** Large tablets / small laptops */
  lg: 1024,
  /** Desktops */
  xl: 1280,
  /** Large desktops */
  '2xl': 1536,
} as const

/**
 * Hook to detect if a media query matches
 * @param query - CSS media query string (e.g., '(max-width: 1023px)')
 * @returns boolean indicating if the query matches
 */
export function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.matchMedia(query).matches
    }
    return false
  })

  useEffect(() => {
    const mediaQuery = window.matchMedia(query)
    setMatches(mediaQuery.matches)

    const handler = (event: MediaQueryListEvent) => {
      setMatches(event.matches)
    }

    mediaQuery.addEventListener('change', handler)
    return () => mediaQuery.removeEventListener('change', handler)
  }, [query])

  return matches
}

/**
 * Check if viewport is mobile (< 768px)
 * Use for phone-only layouts
 */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.md - 1}px)`)
}

/**
 * Check if viewport is tablet (768px - 1023px)
 * Use for tablet-specific layouts
 */
export function useIsTablet(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.md}px) and (max-width: ${BREAKPOINTS.lg - 1}px)`)
}

/**
 * Check if viewport is mobile or tablet (< 1024px)
 * Use for non-desktop layouts
 */
export function useIsMobileOrTablet(): boolean {
  return useMediaQuery(`(max-width: ${BREAKPOINTS.lg - 1}px)`)
}

/**
 * Check if viewport is desktop (>= 1024px)
 */
export function useIsDesktop(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`)
}

/**
 * Check if viewport is large desktop (>= 1280px)
 */
export function useIsLargeDesktop(): boolean {
  return useMediaQuery(`(min-width: ${BREAKPOINTS.xl}px)`)
}

/**
 * Check if device is in landscape orientation
 */
export function useIsLandscape(): boolean {
  return useMediaQuery('(orientation: landscape)')
}

/**
 * Check if device is in portrait orientation
 */
export function useIsPortrait(): boolean {
  return useMediaQuery('(orientation: portrait)')
}

/**
 * Check if device supports touch
 */
export function useIsTouchDevice(): boolean {
  return useMediaQuery('(hover: none) and (pointer: coarse)')
}

/**
 * Get current breakpoint name
 * Returns the largest breakpoint that the viewport fits within
 */
export function useBreakpoint(): keyof typeof BREAKPOINTS | 'base' {
  const isXs = useMediaQuery(`(min-width: ${BREAKPOINTS.xs}px)`)
  const isSm = useMediaQuery(`(min-width: ${BREAKPOINTS.sm}px)`)
  const isMd = useMediaQuery(`(min-width: ${BREAKPOINTS.md}px)`)
  const isLg = useMediaQuery(`(min-width: ${BREAKPOINTS.lg}px)`)
  const isXl = useMediaQuery(`(min-width: ${BREAKPOINTS.xl}px)`)
  const is2Xl = useMediaQuery(`(min-width: ${BREAKPOINTS['2xl']}px)`)

  if (is2Xl) return '2xl'
  if (isXl) return 'xl'
  if (isLg) return 'lg'
  if (isMd) return 'md'
  if (isSm) return 'sm'
  if (isXs) return 'xs'
  return 'base'
}
